import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, downloadUrl, downloadBulkZip } from '../api/client'
import { useToast } from '../context/useToast'
import PageShell from '../components/ui/PageShell'
import Card from '../components/ui/Card'
import Badge, { getGenreBadgeVariant } from '../components/ui/Badge'
import { PrimaryButton, SecondaryButton, DangerButton } from '../components/ui/Button'
import LazyWaveform from '../components/LazyWaveform'

export default function History() {
  const toast = useToast()
  const navigate = useNavigate()
  const [tracks, setTracks] = useState([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState(null)
  const [bulkSelected, setBulkSelected] = useState({})
  const [bulkDownloading, setBulkDownloading] = useState(false)

  useEffect(() => {
    api.get('/tracks')
      .then((r) => setTracks(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setTracks([]))
      .finally(() => setLoading(false))
  }, [])

  const deleteTrack = async (e, trackId) => {
    e.preventDefault()
    e.stopPropagation()
    if (!window.confirm('Track wirklich löschen?')) return
    setDeletingId(trackId)
    try {
      await api.delete(`/tracks/${trackId}`)
      setTracks((prev) => prev.filter((t) => t.id !== trackId))
      toast('Track gelöscht', 'success')
    } catch (err) {
      toast(err.message || 'Löschen fehlgeschlagen', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const statusLabel = (s) => {
    const map = {
      uploaded: 'Warte',
      analyzing: 'Analyse',
      mastering: 'Mastering',
      completed: 'Fertig',
      failed: 'Fehler',
    }
    return map[s] || s
  }

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }) : '')
  const formatDuration = (sec) => (sec != null ? `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}` : '–')

  const toggleBulk = (id) => setBulkSelected((prev) => ({ ...prev, [id]: !prev[id] }))
  const selectAllCompleted = () => {
    const completed = tracks.filter((t) => t.status === 'completed')
    const next = {}
    completed.forEach((t) => { next[t.id] = true })
    setBulkSelected(next)
  }
  const clearBulk = () => setBulkSelected({})
  const bulkSelectedIds = tracks.filter((t) => bulkSelected[t.id]).map((t) => t.id)
  const bulkWavCount = tracks.filter((t) => bulkSelected[t.id] && t.has_wav_download).length
  const bulkMp3Count = tracks.filter((t) => bulkSelected[t.id] && t.has_mp3_download).length

  const doBulkDownload = async (format) => {
    const ids = format === 'wav' ? tracks.filter((t) => bulkSelected[t.id] && t.has_wav_download).map((t) => t.id) : tracks.filter((t) => bulkSelected[t.id] && t.has_mp3_download).map((t) => t.id)
    if (!ids.length) {
      toast(format === 'wav' ? 'Keine ausgewählten Tracks mit WAV' : 'Keine ausgewählten Tracks mit MP3', 'error')
      return
    }
    setBulkDownloading(true)
    try {
      await downloadBulkZip(ids, format, `masters_${format}.zip`)
      toast(`${ids.length} Track(s) als ZIP gestartet`, 'success')
    } catch (e) {
      toast(e.message || 'Massendownload fehlgeschlagen', 'error')
    } finally {
      setBulkDownloading(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-16">
          <p className="text-white/60">Laden…</p>
        </div>
      </PageShell>
    )
  }

  if (tracks.length === 0) {
    return (
      <PageShell>
        <div className="mx-auto max-w-md py-16 text-center">
          <div className="mb-6 text-6xl opacity-40">♪</div>
          <h2 className="text-xl font-semibold text-white">Noch keine Masters</h2>
          <p className="mt-2 text-sm text-white/60">
            Lade deinen ersten Track hoch – die KI mastert ihn automatisch.
          </p>
          <PrimaryButton
            className="mt-6"
            onClick={() => navigate('/dashboard/upload')}
          >
            Ersten Track hochladen
          </PrimaryButton>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-white">Meine Masters</h1>
          <p className="text-sm text-white/50">{tracks.length} Track{tracks.length !== 1 ? 's' : ''}</p>
        </div>

        {tracks.some((t) => t.status === 'completed') && (
          <Card className="flex flex-wrap items-center gap-4 p-4">
            <span className="text-sm text-white/70">Massendownload:</span>
            <SecondaryButton onClick={selectAllCompleted}>Alle fertigen auswählen</SecondaryButton>
            <SecondaryButton onClick={clearBulk}>Auswahl aufheben</SecondaryButton>
            <PrimaryButton onClick={() => doBulkDownload('wav')} disabled={bulkDownloading || bulkWavCount === 0}>
              {bulkDownloading ? '…' : `Als ZIP (WAV)${bulkWavCount ? ` – ${bulkWavCount}` : ''}`}
            </PrimaryButton>
            <PrimaryButton onClick={() => doBulkDownload('mp3')} disabled={bulkDownloading || bulkMp3Count === 0}>
              {bulkDownloading ? '…' : `Als ZIP (MP3)${bulkMp3Count ? ` – ${bulkMp3Count}` : ''}`}
            </PrimaryButton>
          </Card>
        )}

        <ul className="space-y-4">
          {tracks.map((t) => (
            <li key={t.id}>
              <Card className="group flex flex-col gap-4 transition hover:border-white/20 sm:flex-row sm:items-center">
                {t.status === 'completed' && (
                  <div className="flex items-center border-b border-white/10 px-4 py-2 sm:border-b-0 sm:border-r sm:pr-4">
                    <input type="checkbox" checked={!!bulkSelected[t.id]} onChange={() => toggleBulk(t.id)} className="rounded" onClick={(e) => e.stopPropagation()} aria-label={`${t.title_display || t.original_filename} auswählen`} />
                  </div>
                )}
                <Link to={`/dashboard/tracks/${t.id}`} className="flex min-w-0 flex-1 items-center gap-4 sm:gap-6">
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-white/5">
                    {t.has_cover_download && (
                      <img
                        src={downloadUrl(t.id, 'cover')}
                        alt=""
                        className="relative h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling?.classList.remove('hidden') }}
                      />
                    )}
                    <div className={`absolute inset-0 flex items-center justify-center text-2xl text-white/40 ${t.has_cover_download ? 'hidden' : ''}`} aria-hidden>♪</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-white">
                      {(t.title_display || t.original_filename || '').replace(/\.[^.]+$/, '')}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Badge variant={t.status}>{statusLabel(t.status)}</Badge>
                      {t.genre && <Badge variant={getGenreBadgeVariant(t.genre)}>{t.genre}</Badge>}
                      <span className="text-xs text-white/50">{formatDate(t.created_at)}</span>
                      {t.duration_seconds != null && (
                        <span className="text-xs text-white/50">{formatDuration(t.duration_seconds)}</span>
                      )}
                    </div>
                    {t.status === 'completed' && t.has_wav_download && (
                      <div className="mt-2 w-full max-w-[200px] rounded-lg overflow-hidden bg-white/5">
                        <LazyWaveform
                          audioUrl={downloadUrl(t.id, 'wav')}
                          height={28}
                          showControls={false}
                        />
                      </div>
                    )}
                  </div>
                </Link>
                <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-white/10 pt-4 sm:border-t-0 sm:border-l sm:border-t-0 sm:pt-0 sm:pl-6">
                  <Link
                    to={`/dashboard/tracks/${t.id}`}
                    className="inline-flex rounded-xl border border-white/20 bg-white/5 px-5 py-2.5 text-sm font-medium text-white/90 transition hover:bg-white/10"
                  >
                    Öffnen
                  </Link>
                  {t.status === 'completed' && (
                    <a href={downloadUrl(t.id, 'wav')} download rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      <PrimaryButton>Download</PrimaryButton>
                    </a>
                  )}
                  <DangerButton
                    onClick={(e) => deleteTrack(e, t.id)}
                    disabled={deletingId === t.id}
                  >
                    {deletingId === t.id ? '…' : 'Löschen'}
                  </DangerButton>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      </div>
    </PageShell>
  )
}
